import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, ChevronLeft } from "lucide-react";
import { OFFICERS } from "./AIOfficers";
import { trackOfficerHint, trackOfficerDockOpen, trackOfficerSelected, trackOfficerQuery, trackSkillRun, trackCouncilRun } from "@/lib/analytics";
import {
  detectContext, isAuthenticated, isFullAccount, HIDDEN_PATHS, OPEN_OFFICERS_EVENT,
} from "./officer-dock/dockUtils";
import { DockHint } from "./officer-dock/DockHint";
import { DockTrigger } from "./officer-dock/DockTrigger";
import { OfficerPickerView } from "./officer-dock/OfficerPickerView";
import { SkillResultView } from "./officer-dock/SkillResultView";
import { DemoCtaView } from "./officer-dock/DemoCtaView";
import { ChatView } from "./officer-dock/ChatView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Universal floating chat dock — works across ALL tools.
 * Detects current page from URL and passes context to the AI Officer.
 * Can be opened from anywhere via window event "proxima:open-officers".
 */
export const FloatingOfficerDock = () => {
  const location = useLocation();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [fullAccount, setFullAccount] = useState(isFullAccount());
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("picker"); // 'picker' | 'chat' | 'demo_cta' | 'skill_result'
  const [selectedOfficerId, setSelectedOfficerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [skills, setSkills] = useState([]);
  const [runningSkill, setRunningSkill] = useState(null);
  const [skillResult, setSkillResult] = useState(null);
  const [showHint, setShowHint] = useState(false);

  // Re-check auth on route change
  useEffect(() => {
    setAuthed(isAuthenticated());
    setFullAccount(isFullAccount());
  }, [location.pathname]);

  // Global open event (Navbar "AI Officers" item, demo banners, etc.)
  useEffect(() => {
    const openHandler = () => {
      setAuthed(isAuthenticated());
      setFullAccount(isFullAccount());
      setOpen(true);
      setShowHint(false);
      trackOfficerDockOpen(detectContext(window.location.pathname).type);
      try { localStorage.setItem("proxima_officer_hint_seen", "1"); } catch (e) { /* noop */ }
    };
    window.addEventListener(OPEN_OFFICERS_EVENT, openHandler);
    return () => window.removeEventListener(OPEN_OFFICERS_EVENT, openHandler);
  }, []);

  // Hide on auth/legal pages or for unauthenticated users
  const hideHere = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p)) || location.pathname === "/";

  // Detect tool from URL for skill filtering
  const currentCtx = detectContext(location.pathname);
  const toolForSkills =
    currentCtx.type === "political" ? "political"
    : currentCtx.type === "crisis_simulator" ? "crisis"
    : currentCtx.type === "sentiment" ? "sentiment"
    : currentCtx.type === "brief" ? "brief"
    : "social";

  // Load skills for current tool when authenticated dock opens
  useEffect(() => {
    if (!fullAccount || !open) return;
    axios.get(`${API}/agents/skills?tool=${toolForSkills}`).then(r => {
      setSkills(r.data?.skills || []);
    }).catch(() => setSkills([]));
  }, [fullAccount, open, toolForSkills]);

  // First-time tooltip: show 5s if user hasn't seen it before and dock is closed
  useEffect(() => {
    if (!authed || hideHere || open) return;
    try {
      if (localStorage.getItem("proxima_officer_hint_seen") === "1") return;
    } catch (e) { return; }
    const showT = setTimeout(() => {
      setShowHint(true);
      trackOfficerHint("shown");
    }, 1500);
    const hideT = setTimeout(() => {
      setShowHint(false);
      try { localStorage.setItem("proxima_officer_hint_seen", "1"); } catch (e) { /* noop */ }
      trackOfficerHint("auto_dismissed");
    }, 9500);
    return () => { clearTimeout(showT); clearTimeout(hideT); };
  }, [authed, hideHere, open]);

  if (!authed || hideHere) return null;

  const officer = OFFICERS.find((o) => o.id === selectedOfficerId);

  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem("proxima_officer_hint_seen", "1"); } catch (e) { /* noop */ }
    trackOfficerHint("dismissed");
  };

  const openDock = () => {
    setOpen(true);
    setShowHint(false);
    trackOfficerDockOpen(detectContext(location.pathname).type);
    if (showHint) trackOfficerHint("clicked");
    try { localStorage.setItem("proxima_officer_hint_seen", "1"); } catch (e) { /* noop */ }
  };

  const runSkill = async (skill) => {
    setRunningSkill(skill.id);
    setSkillResult(null);
    trackSkillRun(skill.id, toolForSkills);
    try {
      const body = { context_type: currentCtx.type, context_id: currentCtx.id };
      if (skill.output_type === "image" || skill.output_type === "image_with_text") {
        const promptVal = window.prompt(`Describe la imagen para ${skill.label}:`, "Banner anuncio institucional, tono profesional");
        if (!promptVal) { setRunningSkill(null); return; }
        body.input = promptVal;
        body.aspect = "1:1";
      }
      const res = await axios.post(`${API}/agents/skills/${skill.id}`, body, { timeout: 120000 });
      setSkillResult({ skill, ...res.data });
      setView("skill_result");
    } catch (e) {
      setSkillResult({ skill, error: e?.response?.data?.detail || "Error ejecutando skill" });
      setView("skill_result");
    }
    setRunningSkill(null);
  };

  const runCouncil = async () => {
    const question = window.prompt(
      "Pregunta para Council Mode (3 officers debatirán + síntesis ejecutiva):",
      "¿Cuál es el riesgo reputacional más urgente en este contexto y qué deberíamos hacer en las próximas 48h?"
    );
    if (!question || question.trim().length < 3) return;
    setRunningSkill("council");
    setSkillResult(null);
    trackCouncilRun(currentCtx.type);
    try {
      const t0 = Date.now();
      const res = await axios.post(
        `${API}/officers/council`,
        {
          officer_ids: ["analyst", "crisis", "executive"],
          question: question.trim(),
          context_type: currentCtx.type,
          context_id: currentCtx.id,
        },
        { timeout: 90000 }
      );
      setSkillResult({
        skill: { id: "council", label: "Council Mode · 3 officers" },
        council: { ...res.data, latency_ms: res.data.latency_ms ?? (Date.now() - t0) },
      });
      setView("skill_result");
    } catch (e) {
      setSkillResult({
        skill: { id: "council", label: "Council Mode" },
        error: e?.response?.data?.detail || "Council Mode no disponible",
      });
      setView("skill_result");
    }
    setRunningSkill(null);
  };

  const startChat = (oId) => {
    setSelectedOfficerId(oId);
    setMessages([]);
    setSessionId(null);
    trackOfficerSelected(oId);
    // Demo users -> conversion CTA instead of chat
    setView(fullAccount ? "chat" : "demo_cta");
  };

  const backToPicker = () => {
    setView("picker");
    setSelectedOfficerId(null);
    setMessages([]);
  };

  const sendQuestion = async (forcedText) => {
    const q = (typeof forcedText === "string" ? forcedText : input).trim();
    if (!q || sending) return;
    if (typeof forcedText !== "string") setInput("");
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setSending(true);

    const ctx = detectContext(location.pathname);
    trackOfficerQuery(selectedOfficerId, ctx.type);
    try {
      const res = await axios.post(
        `${API}/officers/ask`,
        {
          officer_id: selectedOfficerId,
          question: q,
          context_type: ctx.type,
          context_id: ctx.id,
          session_id: sessionId,
          history: messages.slice(-4),
        },
        { timeout: 60000 }
      );
      setSessionId(res.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.answer, latency_ms: res.data.latency_ms, context_type: ctx.type },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${e?.response?.data?.detail || "Officer no disponible"}`, error: true },
      ]);
    }
    setSending(false);
  };

  return (
    <>
      {/* First-time tooltip */}
      <AnimatePresence>
        {showHint && !open && <DockHint onDismiss={dismissHint} />}
      </AnimatePresence>

      {/* Floating trigger button */}
      <AnimatePresence>
        {!open && <DockTrigger onClick={openDock} />}
      </AnimatePresence>

      {/* Dock panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] sm:w-[420px] max-h-[640px] bg-[#0A0A0F] border border-white/[0.08] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="floating-officer-dock-panel"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                {view === "chat" && (
                  <button onClick={backToPicker} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="floating-dock-back">
                    <ChevronLeft className="w-4 h-4 text-gray-400" />
                  </button>
                )}
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-purple-400 font-medium">
                    {view === "picker" ? "AI OFFICERS" : (officer?.title || "AI OFFICER")}
                  </p>
                  <p className="text-sm font-bold text-white leading-tight">
                    {view === "picker" ? "Consultar especialista" : (officer?.name || "")}
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="floating-dock-close">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Body */}
            {view === "picker" ? (
              <OfficerPickerView
                fullAccount={fullAccount}
                skills={skills}
                toolForSkills={toolForSkills}
                runningSkill={runningSkill}
                onRunSkill={runSkill}
                onRunCouncil={runCouncil}
                onSelectOfficer={startChat}
              />
            ) : view === "skill_result" ? (
              <SkillResultView skillResult={skillResult} onBack={() => setView("picker")} />
            ) : view === "demo_cta" ? (
              <DemoCtaView officer={officer} onBack={backToPicker} />
            ) : (
              <ChatView
                officer={officer}
                officerId={selectedOfficerId}
                messages={messages}
                sending={sending}
                input={input}
                setInput={setInput}
                onSend={sendQuestion}
                contextType={detectContext(location.pathname).type}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingOfficerDock;
